import { createFileRoute, useSearch } from "@tanstack/react-router";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import type { Partner } from "@/lib/auth";
import { setPreferredPartnerId } from "@/lib/auth/token";

export const Route = createFileRoute("/flex/dashboard/select")({
	validateSearch: (search: Record<string, unknown>) => ({
		next: (search.next as string) || "",
		action: typeof search.action === "string" ? search.action : undefined,
	}),
	component: SelectPartnerPage,
});

interface PartnerGroup {
	parentId: number;
	parent: Partner | null;
	parentName: string | null;
	children: Partner[];
}

function SelectPartnerPage() {
	const { partners } = Route.useRouteContext();
	const { next, action } = useSearch({ from: "/flex/dashboard/select" });
	const [selected, setSelected] = useState<string>("");
	const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

	// Group partners by parent-child relationship
	const groupedPartners = useMemo(() => {
		const groups = new Map<number, PartnerGroup>();

		partners.forEach((item) => {
			const partnerInfo = item.partner;
			const parentId = partnerInfo?.parent_id;

			if (partnerInfo?.is_active) {
				if (!parentId) {
					// Root partner
					if (!groups.has(partnerInfo.id)) {
						groups.set(partnerInfo.id, {
							parentId: partnerInfo.id,
							parent: item,
							parentName: partnerInfo.name,
							children: [],
						});
					} else {
						const existing = groups.get(partnerInfo.id);
						if (existing) {
							existing.parent = item;
							existing.parentName = partnerInfo.name;
						}
					}
				} else {
					// Child partner (sub-partner)
					if (!groups.has(parentId)) {
						groups.set(parentId, {
							parentId,
							parent: null,
							parentName: item.parent_name || null,
							children: [],
						});
					}
					groups.get(parentId)?.children.push(item);
				}
			}
		});

		return Array.from(groups.values());
	}, [partners]);

	const toggleGroup = (parentId: number) => {
		setExpandedGroups((prev) => {
			const next = new Set(prev);
			if (next.has(parentId)) {
				next.delete(parentId);
			} else {
				next.add(parentId);
			}
			return next;
		});
	};

	const handleSelect = () => {
		if (!selected) return;

		setPreferredPartnerId(selected);

		// Navigate to the next page or dashboard index
		let destination = "/flex/dashboard/";
		if (next && next !== "" && next !== "dashboard") {
			destination = `/flex/dashboard/${next}`;
		}
		if (action) {
			destination += `${destination.includes("?") ? "&" : "?"}action=${encodeURIComponent(action)}`;
		}
		window.location.replace(destination);
	};

	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
			<div className="w-full max-w-lg">
				<div className="rounded-2xl bg-white p-8 shadow-lg">
					<div className="mb-8">
						<h1 className="text-xl font-semibold text-gray-900">
							This email is registered with multiple accounts
						</h1>
						<p className="mt-2 text-sm text-gray-500">
							Choose an account to access the portal
						</p>
					</div>

					<div className="space-y-3 mb-8 max-h-80 overflow-y-auto">
						{groupedPartners.map((group) => {
							const isExpanded = expandedGroups.has(group.parentId);
							const hasChildren = group.children.length > 0;
							const parentName =
								group.parent?.partner?.name ||
								group.parentName ||
								"Unknown Partner";

							return (
								<div
									key={group.parentId}
									className="rounded-lg border border-gray-200 overflow-hidden"
								>
									{/* Parent Partner Row */}
									<div
										className={`flex items-center justify-between px-4 py-4 ${
											selected === group.parent?.partner_id?.toString()
												? "bg-gray-50"
												: "bg-white"
										}`}
									>
										<div className="flex items-center gap-3 flex-1 min-w-0">
											{group.parent ? (
												<input
													type="radio"
													id={`partner-${group.parentId}`}
													name="partner"
													value={group.parent.partner_id}
													checked={
														selected === group.parent.partner_id.toString()
													}
													onChange={() =>
														setSelected(
															group.parent?.partner_id.toString() ?? "",
														)
													}
													className="h-4 w-4 text-blue-600 border-gray-300 flex-shrink-0 cursor-pointer"
												/>
											) : (
												<div className="w-4 flex-shrink-0" />
											)}
											<label
												htmlFor={`partner-${group.parentId}`}
												className="text-sm font-medium text-gray-900 truncate cursor-pointer"
											>
												{parentName}
											</label>
										</div>

										{hasChildren && (
											<button
												type="button"
												onClick={() => toggleGroup(group.parentId)}
												className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 flex-shrink-0 ml-3"
											>
												<span>
													{group.children.length} subpartner
													{group.children.length > 1 ? "s" : ""}
												</span>
												{isExpanded ? (
													<ChevronUp className="h-4 w-4" />
												) : (
													<ChevronDown className="h-4 w-4" />
												)}
											</button>
										)}
									</div>

									{/* Sub-partners (collapsible) */}
									{hasChildren && isExpanded && (
										<div className="border-t border-gray-100 bg-gray-50">
											{group.children.map((child) => (
												<label
													key={child.partner_id}
													className={`flex items-center gap-3 px-4 py-3 pl-11 cursor-pointer hover:bg-gray-100 ${
														selected === child.partner_id.toString()
															? "bg-gray-100"
															: ""
													}`}
												>
													<input
														type="radio"
														name="partner"
														value={child.partner_id}
														checked={selected === child.partner_id.toString()}
														onChange={() =>
															setSelected(child.partner_id.toString())
														}
														className="h-4 w-4 text-blue-600 border-gray-300"
													/>
													<span className="text-sm text-gray-700">
														{child.partner?.name || child.partner_details?.name}
													</span>
												</label>
											))}
										</div>
									)}
								</div>
							);
						})}
					</div>

					<Button
						onClick={handleSelect}
						disabled={!selected}
						className="w-full h-12 text-base font-medium bg-gray-100 hover:bg-gray-200 text-gray-900 disabled:bg-gray-100 disabled:text-gray-400"
						variant="secondary"
					>
						Continue
					</Button>
				</div>
			</div>
		</div>
	);
}
